import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BadgeTypeValues } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Badge from "@insite/mobius/Badge";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getHideClearanceBadge, isClearanceBadge } from "@tSurya/Common/Utilities/websiteUtils";
import { TYPOGRAPHY } from "@tSurya/theme/design";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import CustomApplicationState from "../../Store/Data/CustomApplicationState/CustomApplicationState";

const enum fields {
    numberOfBadgesToDisplay = "numberOfBadgesToDisplay",
}

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: CustomApplicationState) => ({
    hideClearanceBadge: getHideClearanceBadge(state),
});

export interface CustomProductDetailsTextBadgeStyles {
    container?: InjectableCss;
    badge?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageCss?: JSX.IntrinsicElements["img"];
}

export const customTextBadgeStyles: CustomProductDetailsTextBadgeStyles = {
    container: {
        css: css`
            display: flex;
            column-gap: 8px;
            flex-wrap: wrap;
        `,
    },
    badgeText: {
        css: css`
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.smallest};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            width: 100%;
            white-space: nowrap;
            ${wrapInContainerStyles}
        `,
    },
    badgeImageCss: {
        style: {
            maxWidth: "260px",
            maxHeight: "24px",
            objectFit: "contain",
        },
    },
};

const styles = customTextBadgeStyles;

const CustomProductDetailsTextBadgeWidget = ({
    product: { badges },
    fields: { numberOfBadgesToDisplay },
    hideClearanceBadge,
}: Props) => {
    const textBadges = badges
        ?.filter(badge => badge.badgeType === BadgeTypeValues.Text && badge.displayText)
        .filter(badge => !hideClearanceBadge || !isClearanceBadge(badge));

    const imageBadges = badges
        ?.filter(badge => badge.badgeType === BadgeTypeValues.Image && badge.largeImageBadgePath)
        .filter(badge => !hideClearanceBadge || !isClearanceBadge(badge));

    const combinedBadges = [...(imageBadges || []), ...(textBadges || [])];

    return (
        <StyledWrapper {...styles.container} data-test-selector="textBadges">
            {combinedBadges.slice(0, numberOfBadgesToDisplay).map(badge => (
                <StyledWrapper key={badge.id} {...styles.badge}>
                    {badge.badgeType === BadgeTypeValues.Image ? (
                        <Badge
                            type={badge.badgeType}
                            value={badge.largeImageBadgePath}
                            imageBadgeProps={{ ...styles.badgeImageCss }}
                        />
                    ) : (
                        <Badge
                            type={badge.badgeType}
                            value={badge.displayText}
                            badgeTextColor={badge.textColorHexCode}
                            badgeBackgroundColor={badge.badgeColorHexCode}
                            badgeTextRounding={badge.badgeStyle === "Round" ? 20 : 0}
                            textBadgeCss={styles.badgeText}
                            {...styles.badge}
                        />
                    )}
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(CustomProductDetailsTextBadgeWidget)),
    definition: {
        displayName: "Sur Text Badge",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.numberOfBadgesToDisplay,
                displayName: "Number of badges to display",
                editorTemplate: "DropDownField",
                isRequired: true,
                defaultValue: "8",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                    { displayName: "19", value: "19" },
                    { displayName: "20", value: "20" },
                ],
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
